import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import * as api from './api';
import type { SearchParams, CreateUserInput, UpdateUserInput, ArticleInput } from '@shared/schemas';

export function useSession() {
  return useQuery({
    queryKey: ['session'],
    queryFn: api.getSession,
    staleTime: 60_000,
    retry: false,
  });
}

export function useSearch(params: Partial<SearchParams>, enabled = true) {
  return useQuery({
    queryKey: ['search', params],
    queryFn: () => api.searchArticles(params),
    placeholderData: (prev) => prev,
    enabled,
  });
}

/**
 * Ricerca con accumulo ("carica altri"): ogni pagina aggiunge i risultati alla
 * lista invece di sostituirli. La chiave esclude `page` (gestita internamente da
 * useInfiniteQuery). `getNextPageParam` si ferma all'ultima pagina (entro il cap
 * MAX_RESULTS del server, oltre cui `totalPages` non cresce e compare "raffina").
 */
export function useInfiniteSearch(params: Partial<SearchParams>, enabled = true) {
  const rest: Partial<SearchParams> = { ...params };
  delete rest.page;
  return useInfiniteQuery({
    queryKey: ['search', rest],
    queryFn: ({ pageParam }) => api.searchArticles({ ...rest, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page < last.totalPages ? last.page + 1 : undefined),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useArticle(id: string | undefined) {
  return useQuery({
    queryKey: ['article', id],
    queryFn: () => api.getArticle(id!),
    enabled: !!id,
  });
}

export function useFonti() {
  return useQuery({
    queryKey: ['fonti'],
    queryFn: api.getFonti,
    staleTime: 5 * 60_000,
  });
}

export function useDateIndex() {
  return useQuery({
    queryKey: ['dateIndex'],
    queryFn: api.getDateIndex,
    staleTime: 5 * 60_000,
  });
}

export function useSignIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ username, password }: { username: string; password: string }) =>
      api.signIn(username, password),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['session'] }),
  });
}

export function useSignOut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.signOut,
    onSuccess: () => {
      qc.clear();
    },
  });
}

// ─── Admin ───
export function useUsers() {
  return useQuery({ queryKey: ['admin', 'users'], queryFn: api.listUsers });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateUserInput) => api.createUserApi(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateUserInput }) =>
      api.updateUserApi(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteUserApi(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export function useCreateArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ArticleInput) => api.createArticleApi(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['search'] });
      qc.invalidateQueries({ queryKey: ['fonti'] });
      qc.invalidateQueries({ queryKey: ['dateIndex'] });
    },
  });
}

export function useUpdateArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ArticleInput }) =>
      api.updateArticleApi(id, input),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['article', vars.id] });
      qc.invalidateQueries({ queryKey: ['search'] });
      qc.invalidateQueries({ queryKey: ['fonti'] });
      qc.invalidateQueries({ queryKey: ['dateIndex'] });
    },
  });
}

export function useDeleteArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteArticleApi(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['search'] });
      qc.invalidateQueries({ queryKey: ['fonti'] });
      qc.invalidateQueries({ queryKey: ['dateIndex'] });
    },
  });
}
